/**
 * /admin/beta — admin dashboard for managing beta invites + viewing feedback.
 *
 * Three sections:
 *   1. Stats strip (invites issued, redeemed, avg rating, feedback count).
 *   2. Invite list + "+ New invite" modal.
 *   3. Feedback log (most recent first).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Copy, MessageSquare, Sparkles, Users, Star } from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const AdminBeta = () => {
  const [stats, setStats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ invitee_email: '', invitee_name: '', note: '', ttl_days: 7 });

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('token') || ''}` };

  const refresh = async () => {
    try {
      const [s, i, f] = await Promise.all([
        axios.get(`${API}/beta/stats`, { headers: authHeader }),
        axios.get(`${API}/beta/invites`, { headers: authHeader }),
        axios.get(`${API}/beta/feedback?limit=50`, { headers: authHeader }),
      ]);
      setStats(s.data);
      setInvites(i.data.invites || []);
      setFeedback(f.data.feedback || []);
    } catch (e) {
      toast.error('Failed to load beta admin (are you logged in as admin?)');
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const createInvite = async () => {
    try {
      const res = await axios.post(
        `${API}/beta/invites`,
        { ...form, ttl_days: Number(form.ttl_days) || 7 },
        { headers: authHeader },
      );
      navigator.clipboard?.writeText(res.data.invite_url).catch(() => {});
      toast.success('Invite created · URL copied to clipboard');
      setForm({ invitee_email: '', invitee_name: '', note: '', ttl_days: 7 });
      setCreating(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invite');
    }
  };

  const revokeInvite = async (id) => {
    try {
      await axios.delete(`${API}/beta/invites/${id}`, { headers: authHeader });
      toast.success('Invite revoked');
      refresh();
    } catch (e) {
      toast.error('Revoke failed');
    }
  };

  const copyUrl = (token) => {
    const url = `${BASE}/beta?invite=${token}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    toast.success('Invite URL copied');
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="admin-beta-page">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <Link to="/admin/pre-flight" className="text-xs text-slate-600 hover:text-emerald-400 mb-2 inline-flex items-center gap-1" data-testid="admin-beta-back"><ArrowLeft className="w-3 h-3" /> Pre-flight</Link>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"><BrandTitle>Beta Testers</BrandTitle></h1>
            <p className="text-slate-600 mt-2 max-w-2xl">Mint a 7-day invite for a friendly prospect. They get an isolated sandbox + seed data. You see their feedback inline.</p>
          </div>
          <Button onClick={() => setCreating(!creating)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="admin-beta-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New invite
          </Button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-900 text-slate-100 border-slate-800"><CardContent className="p-5">
            <div className="flex items-center justify-between mb-1"><Users className="w-4 h-4 text-emerald-400" /><span className="text-xs text-slate-400">Invites</span></div>
            <div className="text-2xl font-bold text-white tabular-nums" data-testid="admin-beta-invites-total">{stats?.invites_total ?? '—'}</div>
          </CardContent></Card>
          <Card className="bg-slate-900 text-slate-100 border-slate-800"><CardContent className="p-5">
            <div className="flex items-center justify-between mb-1"><Sparkles className="w-4 h-4 text-emerald-400" /><span className="text-xs text-slate-400">Redeemed</span></div>
            <div className="text-2xl font-bold text-white tabular-nums">{stats?.redeemed ?? '—'} <span className="text-xs text-slate-400 font-normal">({stats?.redemption_rate_percent ?? 0}%)</span></div>
          </CardContent></Card>
          <Card className="bg-slate-900 text-slate-100 border-slate-800"><CardContent className="p-5">
            <div className="flex items-center justify-between mb-1"><MessageSquare className="w-4 h-4 text-cyan-400" /><span className="text-xs text-slate-400">Feedback</span></div>
            <div className="text-2xl font-bold text-white tabular-nums">{stats?.feedback_count ?? '—'}</div>
          </CardContent></Card>
          <Card className="bg-slate-900 text-slate-100 border-slate-800"><CardContent className="p-5">
            <div className="flex items-center justify-between mb-1"><Star className="w-4 h-4 text-amber-400" /><span className="text-xs text-slate-400">Avg rating</span></div>
            <div className="text-2xl font-bold text-white tabular-nums">{stats?.avg_rating ?? '—'} <span className="text-xs text-slate-400 font-normal">/ 5</span></div>
          </CardContent></Card>
        </div>

        {/* New invite form */}
        {creating && (
          <Card className="bg-emerald-500/5 border-emerald-500/30 mb-6" data-testid="admin-beta-form">
            <CardHeader><CardTitle className="text-slate-900 text-base">Create beta invite</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-300 font-semibold">Invitee name</Label><Input value={form.invitee_name} onChange={(e) => setForm({ ...form, invitee_name: e.target.value })} placeholder="Maya from Acme" className="bg-slate-950 border-slate-700" /></div>
                <div><Label className="text-xs text-slate-300 font-semibold">Invitee email (optional)</Label><Input type="email" value={form.invitee_email} onChange={(e) => setForm({ ...form, invitee_email: e.target.value })} placeholder="maya@acme.io" className="bg-slate-950 border-slate-700" /></div>
              </div>
              <div><Label className="text-xs text-slate-300 font-semibold">Note (private)</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Met at FinOps meetup; ~$120K/mo AWS spend" rows={2} className="bg-slate-950 border-slate-700" /></div>
              <div><Label className="text-xs text-slate-300 font-semibold">Valid for (days)</Label><Input type="number" min={1} max={30} value={form.ttl_days} onChange={(e) => setForm({ ...form, ttl_days: e.target.value })} className="bg-slate-950 border-slate-700 w-32" /></div>
              <div className="flex gap-2 pt-1">
                <Button onClick={createInvite} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="admin-beta-save-btn">Create + copy URL</Button>
                <Button onClick={() => setCreating(false)} variant="ghost" className="text-slate-600">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invites table */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800 mb-6">
          <CardHeader><CardTitle className="text-white text-base">All invites ({invites.length})</CardTitle></CardHeader>
          <CardContent>
            {invites.length === 0 && <p className="text-sm text-slate-400">No invites yet. Click "New invite" above.</p>}
            <div className="space-y-2">
              {invites.map((inv) => (
                <div key={inv.invite_id} className="grid sm:grid-cols-12 gap-2 items-center border border-slate-800 rounded-md p-3" data-testid={`admin-beta-invite-${inv.invite_id}`}>
                  <div className="sm:col-span-4">
                    <div className="text-sm text-white font-medium">{inv.invitee_name || 'Unnamed'}</div>
                    <div className="text-xs text-slate-400">{inv.invitee_email || '—'}</div>
                  </div>
                  <div className="sm:col-span-3 text-xs">
                    {inv.revoked && <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40">Revoked</Badge>}
                    {!inv.revoked && inv.redeemed_at && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Redeemed</Badge>}
                    {!inv.revoked && !inv.redeemed_at && <Badge className="bg-emerald-500/20 text-cyan-300 border-emerald-500/40">Pending</Badge>}
                  </div>
                  <div className="sm:col-span-3 text-xs text-slate-400">
                    Expires {inv.expires_at?.slice(0, 10)}
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => copyUrl(inv.token)} className="h-7 text-emerald-400" data-testid={`admin-beta-copy-${inv.invite_id}`}><Copy className="w-3 h-3" /></Button>
                    {!inv.revoked && <Button size="sm" variant="ghost" onClick={() => revokeInvite(inv.invite_id)} className="h-7 text-rose-400" data-testid={`admin-beta-revoke-${inv.invite_id}`}><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Feedback log */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800">
          <CardHeader><CardTitle className="text-white text-base">Feedback ({feedback.length})</CardTitle></CardHeader>
          <CardContent>
            {feedback.length === 0 && <p className="text-sm text-slate-400">No feedback yet. Beta testers can submit via the floating widget on any page.</p>}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {feedback.map((f) => (
                <div key={f.feedback_id} className="border-l-2 border-emerald-500/50 pl-3 py-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-amber-400">{'★'.repeat(f.rating)}</span>
                    <span className="text-slate-400 font-medium">{f.user_email || 'anonymous'}</span>
                    <span className="text-slate-400">· {f.page}</span>
                    {f.is_beta && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">BETA</Badge>}
                  </div>
                  <p className="text-sm text-slate-300 mt-1">{f.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{f.submitted_at?.slice(0, 16).replace('T', ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminBeta;
